/**
 * Reusable Tab Component
 *
 * Usage:
 * 1. Wrap your tab interface in a container with data-tabs attribute
 * 2. Add data-tab to tab buttons with unique identifiers
 * 3. Add data-tab-pane to content sections matching button identifiers
 * 4. Optional: Add data-group to buttons and data-card-group to cards for dynamic card switching
 *
 * Example:
 * <div data-tabs="my-tabs">
 *   <div data-tabs-cards>
 *     <div data-card-group="group1">Card 1</div>
 *     <div data-card-group="group2" class="hidden">Card 2</div>
 *   </div>
 *
 *   <div data-tabs-nav>
 *     <button data-tab="tab1" data-group="group1">Tab 1</button>
 *     <button data-tab="tab2" data-group="group2">Tab 2</button>
 *   </div>
 *
 *   <div data-tabs-content>
 *     <div data-tab-pane="tab1">Content 1</div>
 *     <div data-tab-pane="tab2" class="hidden">Content 2</div>
 *   </div>
 * </div>
 */

export class Tabs {
  // Static storage for sub-tab selections across all instances
  // Format: { 'expense-tabs': 'pending', 'income-tabs': 'awaiting' }
  static subTabMemory = new Map();

  constructor(container) {
    this.container = container;
    this.tabsId = container.dataset.tabs;

    // Use scoped selectors to only find direct children, not nested tabs
    // This prevents parent tabs from interfering with nested sub-tabs
    const nav = container.querySelector(':scope > [data-tabs-nav], :scope > * > [data-tabs-nav]');
    const content = container.querySelector(':scope > [data-tabs-content], :scope > * > [data-tabs-content]');
    const cardsContainer = container.querySelector(':scope > [data-tabs-cards], :scope > * > [data-tabs-cards]');

    // Only select buttons/panes that are direct children of nav/content
    this.buttons = nav ? Array.from(nav.querySelectorAll(':scope > [data-tab]')) : [];
    this.panes = content ? Array.from(content.querySelectorAll(':scope > [data-tab-pane]')) : [];
    this.cards = cardsContainer ? Array.from(cardsContainer.querySelectorAll(':scope > [data-card-group]')) : [];

    this.init();
  }

  init() {
    // Add click handlers to all tab buttons
    this.buttons.forEach(button => {
      button.addEventListener('click', () => this.handleTabClick(button));
    });
  }

  handleTabClick(activeButton) {
    const targetTab = activeButton.dataset.tab;
    const targetGroup = activeButton.dataset.group;

    // Store this tab selection in memory
    Tabs.subTabMemory.set(this.tabsId, targetTab);

    // Update button states
    this.updateButtons(activeButton);

    // Switch tab content
    this.switchContent(targetTab);

    // Handle nested tabs: restore previous selection or select first tab
    this.handleNestedTabs(targetTab);

    // Switch cards if group is specified
    if (targetGroup) {
      this.switchCards(targetGroup);
    }

    // Dispatch custom event for external listeners
    this.container.dispatchEvent(new CustomEvent('tab-changed', {
      detail: {
        tab: targetTab,
        group: targetGroup,
        tabsId: this.tabsId
      },
      bubbles: true
    }));
  }

  updateButtons(activeButton) {
    this.buttons.forEach(btn => {
      if (btn === activeButton) {
        btn.classList.add('active');
        btn.classList.remove('text-gray-500', 'hover:text-gray-700');
        btn.classList.add('text-blue-600', 'border-b-2', 'border-blue-600');
      } else {
        btn.classList.remove('active');
        btn.classList.remove('text-blue-600', 'border-b-2', 'border-blue-600');
        btn.classList.add('text-gray-500', 'hover:text-gray-700');
      }
    });
  }

  switchContent(targetTab) {
    this.panes.forEach(pane => {
      if (pane.dataset.tabPane === targetTab) {
        pane.classList.remove('hidden');
      } else {
        pane.classList.add('hidden');
      }
    });
  }

  switchCards(targetGroup) {
    this.cards.forEach(card => {
      if (card.dataset.cardGroup === targetGroup) {
        card.classList.remove('hidden');
      } else {
        card.classList.add('hidden');
      }
    });
  }

  /**
   * Handle nested tabs: restore previous selection or select first tab
   * This method is called after switching to a new tab to manage sub-tabs
   * @param {string} parentTab - The parent tab that was just activated
   */
  handleNestedTabs(parentTab) {
    // Find the newly visible pane
    const activePane = this.panes.find(pane => pane.dataset.tabPane === parentTab);
    if (!activePane) return;

    // Check if this pane contains nested tabs
    const nestedTabsContainer = activePane.querySelector('[data-tabs]');
    if (!nestedTabsContainer) return;

    const nestedTabsId = nestedTabsContainer.dataset.tabs;
    const nestedTabsInstance = nestedTabsContainer.tabsInstance;

    // If nested tabs instance not initialized yet, wait and retry
    if (!nestedTabsInstance) {
      setTimeout(() => this.handleNestedTabs(parentTab), 50);
      return;
    }

    // Check if we have a stored selection for this nested tab
    const storedSelection = Tabs.subTabMemory.get(nestedTabsId);

    if (storedSelection) {
      // Restore the stored selection
      nestedTabsInstance.switchTo(storedSelection);
    } else {
      // No stored selection, select the first tab
      const firstButton = nestedTabsInstance.buttons[0];
      if (firstButton) {
        nestedTabsInstance.handleTabClick(firstButton);
      }
    }
  }

  /**
   * Public API: Switch to a specific tab programmatically
   * @param {string} tabId - The data-tab identifier
   */
  switchTo(tabId) {
    const targetButton = Array.from(this.buttons).find(
      btn => btn.dataset.tab === tabId
    );

    if (targetButton) {
      this.handleTabClick(targetButton);
    } else {
      console.warn(`Tab "${tabId}" not found in tabs container "${this.tabsId}"`);
    }
  }
}

/**
 * Auto-initialize all tab containers on the page
 */
export function initTabs() {
  const tabContainers = document.querySelectorAll('[data-tabs]');

  tabContainers.forEach(container => {
    // Store instance on the element for programmatic access
    container.tabsInstance = new Tabs(container);
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTabs);
} else {
  initTabs();
}
